import { _, React, ts, useSelector } from '_core';

import Chart from './chart';
import Form from './form';
import { DraftParams, Params } from './types';

type BacktestAttributionProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  mode: ts.enums.CHART_MODE_ENUM;
  params: Params | DraftParams;
  setParams: (_p: DraftParams) => void;

  queryParams?: { initial?: string };
  goFullScreen?: (_v: string) => void;
  fullScreen?: boolean;
  context?: ts.types.backtest.BacktestAnaylsisContext;
  widgetPaths?: Record<'backtest_portan_chart' | 'portfolio_portan_chart', string>;

  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
};

const BacktestAttribution = ({
  id,
  readOnly,
  widgetKey,
  loadWidgetData,
  params,
  fullScreen,
  setParams,
  mode,
  context,
  widgetPaths,
}: BacktestAttributionProps) => {
  const riskModels = useSelector((state) => state.resources.risk_models);
  const strategies = useSelector((state) => state.resources.strategies);

  const strategyId = (context?.definition as ts.types.portfolio.PortfolioAnalysisContext['definition'])?.strategy_id;
  const currentStrategy = React.useMemo(() => {
    return _.find(strategies, (el) => el.id == strategyId);
  }, [strategyId]);

  const currentRiskModel = React.useMemo(() => {
    return _.find(
      riskModels,
      (el) => el.id == context?.definition?.risk_model_id || el.id == currentStrategy?.definition?.risk_model_id
    );
  }, [context?.definition?.risk_model_id]);

  const currentRiskModelIndustries = currentRiskModel?.definition?.categories?.[0]?.factors;

  const hasBenchmark = !_.isEmpty(context?.definition?.benchmark);

  const fileKey = React.useMemo(
    () => Object.keys(widgetPaths || {}).find((k) => k.endsWith('_portan_chart')) as keyof typeof widgetPaths,
    [widgetPaths]
  );

  const filteredHistories =
    context.definition.frequency == ts.enums.FREQUENCY_ENUM.DAILY
      ? context.context_histories.filter((h) => h !== ts.enums.HISTORY_ENUM_EXTENDED.FIVE_TRADING_DAYS)
      : context.context_histories;

  return mode == 'edit' ? (
    <Form
      params={params}
      setParams={setParams}
      hasBenchmark={hasBenchmark}
      contextRiskModel={currentRiskModel?.id}
      strategyType={currentStrategy && currentStrategy.type}
    />
  ) : (
    <Chart
      id={id}
      readOnly={readOnly}
      widgetKey={widgetKey}
      loadWidgetData={loadWidgetData}
      file={widgetPaths[fileKey]}
      riskModelIndustries={currentRiskModelIndustries}
      fullScreen={fullScreen}
      params={params}
      histories={filteredHistories}
      startDate={context.start_date}
      endDate={context.end_date}
    />
  );
};

export default BacktestAttribution;
